import React, { useMemo, useState } from "react";
import { formatCurrency } from "../helpers/estimateTotals";
import { VARIANCE_CATEGORY } from "../helpers/projectFinancialReport";

const CARD_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(201,168,76,0.25)",
  borderRadius: "14px",
  padding: "16px",
  marginBottom: "16px",
};

const METRIC_STYLE = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: "8px",
  opacity: 0.9,
};

function formatVarianceLabel(category) {
  switch (category) {
    case VARIANCE_CATEGORY.MATCHED:
      return "Matched";
    case VARIANCE_CATEGORY.QUOTE_ONLY:
      return "Not yet used";
    case VARIANCE_CATEGORY.ACTUAL_ONLY:
      return "Extra / unmatched";
    default:
      return category;
  }
}

function VarianceSection({ title, rows = [] }) {
  const [expanded, setExpanded] = useState(false);

  if (!rows.length) return null;

  const visibleRows = expanded ? rows : rows.slice(0, 4);

  return (
    <div style={{ marginTop: "16px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: "14px" }}>{title}</div>
        {rows.length > 4 && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            style={{
              border: "none",
              background: "none",
              color: "#c9a84c",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            {expanded ? "Show less" : `Show all (${rows.length})`}
          </button>
        )}
      </div>

      {visibleRows.map((row) => (
        <div
          key={`${row.lineSnapshotId || row.description}-${row.category}`}
          style={{
            ...CARD_STYLE,
            marginBottom: "8px",
            padding: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              marginBottom: "4px",
            }}
          >
            <div style={{ fontWeight: 600 }}>{row.description}</div>
            <div style={{ fontSize: "12px", opacity: 0.7 }}>
              {formatVarianceLabel(row.category)}
            </div>
          </div>
          <div style={{ fontSize: "13px", opacity: 0.8, lineHeight: 1.5 }}>
            {row.category !== VARIANCE_CATEGORY.ACTUAL_ONLY && (
              <div>
                Quoted: {row.quotedQuantity} {row.unitLabel}
                {row.quotedLineTotal > 0
                  ? ` · ${formatCurrency(row.quotedLineTotal)}`
                  : ""}
              </div>
            )}
            {row.category !== VARIANCE_CATEGORY.QUOTE_ONLY && (
              <div>
                Actual: {row.actualQuantity} {row.unitLabel}
                {row.actualCost > 0 ? ` · ${formatCurrency(row.actualCost)}` : ""}
              </div>
            )}
            {row.quantityVariance != null &&
              row.category === VARIANCE_CATEGORY.MATCHED && (
                <div>
                  Qty variance: {row.quantityVariance > 0 ? "+" : ""}
                  {row.quantityVariance} {row.unitLabel}
                </div>
              )}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ProjectFinancialPanel({ financialData, loading }) {
  const quoteLines = financialData?.quoteLines || [];
  const materialLines = useMemo(
    () => quoteLines.filter((line) => line.line_type === "material"),
    [quoteLines],
  );
  const labourLines = useMemo(
    () => quoteLines.filter((line) => line.line_type === "labour"),
    [quoteLines],
  );

  if (loading) {
    return (
      <div className="kpi-chart-card" style={{ ...CARD_STYLE, opacity: 0.7 }}>
        Loading financial summary…
      </div>
    );
  }

  if (!financialData) return null;

  const {
    quoted,
    actualCost,
    billed,
    margin,
    hasQuoteSnapshot,
    materialCost,
    labourCost,
    otherCost = 0,
    variance,
  } = financialData;

  return (
    <>
      <div className="kpi-chart-card" style={CARD_STYLE}>
        <div className="section-heading">FINANCIAL SUMMARY</div>
        <p style={{ opacity: 0.7, fontSize: "13px", margin: "8px 0 12px" }}>
          Quoted → Actual Cost → Final Billed (analytics only)
        </p>

        {hasQuoteSnapshot ? (
          <div style={METRIC_STYLE}>
            <span>Quoted</span>
            <span style={{ color: "#c9a84c", fontWeight: 600 }}>
              {formatCurrency(quoted)}
            </span>
          </div>
        ) : (
          <div style={{ ...METRIC_STYLE, opacity: 0.65, fontSize: "13px" }}>
            <span>No quote snapshot (manual project)</span>
          </div>
        )}

        <div style={METRIC_STYLE}>
          <span>Actual Cost</span>
          <span style={{ fontWeight: 600 }}>{formatCurrency(actualCost)}</span>
        </div>

        <div
          style={{
            ...METRIC_STYLE,
            marginLeft: "12px",
            fontSize: "13px",
            opacity: 0.8,
          }}
        >
          <span>Labour</span>
          <span>{formatCurrency(labourCost)}</span>
        </div>
        <div
          style={{
            ...METRIC_STYLE,
            marginLeft: "12px",
            fontSize: "13px",
            opacity: 0.8,
          }}
        >
          <span>Materials</span>
          <span>{formatCurrency(materialCost)}</span>
        </div>
        <div
          style={{
            ...METRIC_STYLE,
            marginLeft: "12px",
            fontSize: "13px",
            opacity: 0.8,
          }}
        >
          <span>Other Costs</span>
          <span>{formatCurrency(otherCost)}</span>
        </div>

        <div style={METRIC_STYLE}>
          <span>Final Billed</span>
          <span style={{ color: "#c9a84c", fontWeight: 600 }}>
            {formatCurrency(billed)}
          </span>
        </div>

        <hr style={{ margin: "12px 0", opacity: 0.25 }} />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 600,
          }}
        >
          <span>Margin (Billed − Actual)</span>
          <span style={{ color: margin >= 0 ? "#4ade80" : "#e06b5a" }}>
            {formatCurrency(margin)}
          </span>
        </div>
      </div>

      {hasQuoteSnapshot && quoteLines.length > 0 && (
        <div className="kpi-chart-card" style={CARD_STYLE}>
          <div className="section-heading">QUOTED SCOPE</div>
          <p style={{ opacity: 0.7, fontSize: "13px", margin: "8px 0 12px" }}>
            Frozen at estimate conversion — reference only, not billing authority.
          </p>

          {materialLines.length > 0 && (
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontWeight: 600, marginBottom: "8px" }}>Materials</div>
              {materialLines.map((line) => (
                <div
                  key={line.line_snapshot_id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "6px",
                    fontSize: "14px",
                  }}
                >
                  <span style={{ opacity: 0.9 }}>
                    {line.description_snapshot}
                    {line.quoted_quantity_snapshot != null && (
                      <span style={{ opacity: 0.65 }}>
                        {" "}
                        · {line.quoted_quantity_snapshot}{" "}
                        {line.unit_label_snapshot || "ea"}
                      </span>
                    )}
                  </span>
                  <span style={{ color: "#c9a84c", flexShrink: 0 }}>
                    {formatCurrency(line.quoted_line_total_snapshot)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {labourLines.length > 0 && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: "8px" }}>Labour</div>
              {labourLines.map((line) => (
                <div
                  key={line.line_snapshot_id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "6px",
                    fontSize: "14px",
                  }}
                >
                  <span style={{ opacity: 0.9 }}>
                    {line.description_snapshot}
                    {line.quoted_quantity_snapshot != null && (
                      <span style={{ opacity: 0.65 }}>
                        {" "}
                        · {line.quoted_quantity_snapshot}{" "}
                        {line.unit_label_snapshot || "hrs"}
                      </span>
                    )}
                  </span>
                  <span style={{ color: "#c9a84c", flexShrink: 0 }}>
                    {formatCurrency(line.quoted_line_total_snapshot)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {hasQuoteSnapshot && variance && (
        <div className="kpi-chart-card" style={CARD_STYLE}>
          <div className="section-heading">VARIANCE</div>
          <VarianceSection title="Materials" rows={variance.material} />
          <VarianceSection title="Labour" rows={variance.labour} />
        </div>
      )}
    </>
  );
}
